import React from "react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type { CustomCsvImportResponse } from "../../../types/customCsvImport";
import type { CustomCsvWizardState } from "../../../types/customCsvImport";

export interface CustomCsvImportStepProps {
  state: CustomCsvWizardState;
  isLoading: boolean;
  error: string | null;
  result: CustomCsvImportResponse | null;
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
  onImport: () => void;
  hideImportButton?: boolean;
  /** Demo — no DB import; continue to analysis instead. */
  demoMode?: boolean;
}

const CustomCsvImportStep = ({
  state,
  error,
  result,
  onStateChange,
  demoMode = false,
}: CustomCsvImportStepProps): React.ReactElement => {
  const preview = state.preview;
  const saveTemplate = state.saveProfile || state.saveTemplate;

  if (result) {
    const stats = result.sync_stats;
    return (
      <Alert variant="success" className={BROKER_ALERT_CLASS}>
        <BrokerAlertIcon tone="success" />
        <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
          Import complete: {stats.created} new, {stats.updated} updated, {stats.total} total trades
          for <span className={BROKER_ALERT_TITLE_CLASS}>{result.account_name}</span>.
        </AlertDescription>
      </Alert>
    );
  }

  if (demoMode) {
    return (
      <div className="flex flex-col gap-4" data-custom-csv-import-step="" data-demo="">
        <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
          Continue with{" "}
          <span className={BROKER_ALERT_TITLE_CLASS}>{state.csvFile?.name || "your CSV"}</span>{" "}
          to build the demo Entry Analysis. Nothing is saved to a Tessera account.
        </p>
        {!preview?.can_import ? (
          <Alert variant="warning" className={BROKER_ALERT_CLASS}>
            <BrokerAlertIcon tone="warning" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              Preview must succeed without blocking errors before you can continue.
            </AlertDescription>
          </Alert>
        ) : null}
        {error ? (
          <Alert variant="error" className={BROKER_ALERT_CLASS}>
            <BrokerAlertIcon tone="error" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>{error}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-custom-csv-import-step="">
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
        Import <span className={BROKER_ALERT_TITLE_CLASS}>{state.csvFile?.name}</span> into account{" "}
        <span className={BROKER_ALERT_TITLE_CLASS}>{state.accountName || "—"}</span> (
        {state.displayName || "—"}).
      </p>

      {!preview?.can_import ? (
        <Alert variant="warning" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="warning" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            Preview must succeed without blocking errors before you can import.
          </AlertDescription>
        </Alert>
      ) : null}

      <Field className="gap-2" data-broker-csv-field="">
        <div className="flex min-w-0 items-start gap-2">
          <Checkbox
            id="custom-csv-save-template"
            data-broker-asset-class-checkbox=""
            checked={saveTemplate}
            className="mt-0.5"
            onCheckedChange={(checked) =>
              onStateChange({
                saveProfile: checked === true,
                saveTemplate: checked === true,
              })
            }
          />
          <div className="flex min-w-0 flex-col gap-1">
            <FieldLabel
              htmlFor="custom-csv-save-template"
              className={`${BROKER_ALERT_TITLE_CLASS} cursor-pointer font-normal`}
            >
              Save this mapping as a template for next time
            </FieldLabel>
            {saveTemplate ? (
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                Saved under your template name. Re-importing with the same name updates the existing
                template. Templates are not tied to this account code.
              </FieldDescription>
            ) : null}
          </div>
        </div>
      </Field>

      {error ? (
        <Alert variant="error" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="error" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>{error}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};

export default CustomCsvImportStep;
